import React, { useEffect, useState } from "react";
import {
    fetchSpecialityDoctorList, fetchSpecialtiesList,
    reorderSpecialistDoctors,
    toggleDoctorSpecialityStatus, setDoctorSpecialityPosition
} from "../../Adapters/ApiAdapter";
import { IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import {
    DndContext,
    closestCenter,
    useSensor,
    useSensors,
    PointerSensor,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
    arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    Table, TableRow, TableHead, TableBody, TableCell,
    Paper, Select, MenuItem, FormControl, InputLabel,
    Checkbox, Typography
} from "@mui/material";
import { toast } from "react-toastify";

const SortableRow = ({ doctor, onToggle, onEdit }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: doctor.doctor_specialities_id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        cursor: "move",
    };

    return (
        <TableRow ref={setNodeRef} style={style} hover>
            <TableCell {...attributes} {...listeners}>☰</TableCell>
            <TableCell>{doctor.display_order}</TableCell>
            <TableCell>{doctor.name}</TableCell>
            <TableCell>{doctor.speciality_name}</TableCell>
            <TableCell>
                {/* <Checkbox
                    checked={doctor.doctor_specialities_status === "active" || doctor.doctor_specialities_status === 1 || doctor.doctor_specialities_status === "1"}
                    onChange={(e) =>
                        onToggle(
                            doctor.doctor_specialities_id,         // assuming this is both ID and doctor_id
                            doctor.id,
                            doctor.speciality_id,
                            e.target.checked ? "active" : "inactive"
                        )
                    }
                /> */}
                <Checkbox
                    checked={
                        doctor.doctor_specialities_status === "active" ||
                        doctor.doctor_specialities_status === 1 ||
                        doctor.doctor_specialities_status === "1"
                    }
                    onChange={() => onToggle(doctor)} // ✅ Send the full object
                />
            </TableCell>
            <TableCell>
                <IconButton onClick={() => onEdit(doctor)}>
                    <EditIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

const ListSpecialistDoctors = () => {
    const [specialityId, setSpecialityId] = useState("");
    const [specialties, setSpecialties] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const sensors = useSensors(useSensor(PointerSensor));
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [newPosition, setNewPosition] = useState("");

    useEffect(() => {
        const loadSpecialties = async () => {
            try {
                const res = await fetchSpecialtiesList();
                if (res?.specialities) {
                    setSpecialties(res.specialities);
                } else {
                    toast.error("Failed to fetch specialties");
                }
            } catch (err) {
                toast.error("Error loading specialties");
            }
        };
        loadSpecialties();
    }, []);

    const handleFetchDoctors = async (id) => {
        const res = await fetchSpecialityDoctorList(id);
        if (res?.data) setDoctors(res.data);
    };

    // const handleToggleStatus = async (id, doctor_id, speciality_id, status) => {
    //     try {
    //         await toggleDoctorSpecialityStatus({ id, doctor_id, speciality_id, status });
    //         toast.success("Status updated");
    //         handleFetchDoctors(specialityId);
    //     } catch {
    //         toast.error("Failed to update status");
    //     }
    // };

    const handleToggleStatus = async (doctor) => {
        const newStatus = doctor.doctor_specialities_status === "active" ? "inactive" : "active";

        try {
            await toggleDoctorSpecialityStatus({
                id: doctor.doctor_specialities_id,
                doctor_id: doctor.id,
                speciality_id: specialityId,
                status: newStatus, // ✅ keep the key as "status" here because adapter sends it like that
            });

            const updatedDoctors = doctors.map((d) =>
                d.id === doctor.id
                    ? { ...d, doctor_specialities_status: newStatus } // ✅ update correct field
                    : d
            );
            setDoctors(updatedDoctors);
            toast.success("Status updated");
        } catch (error) {
            toast.error("Failed to update status");
        }
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = doctors.findIndex((doc) => doc.doctor_specialities_id === active.id);
        const newIndex = doctors.findIndex((doc) => doc.doctor_specialities_id === over.id);

        const reordered = arrayMove(doctors, oldIndex, newIndex);

        // Update display_order locally
        const updatedDoctors = reordered.map((doc, index) => ({
            ...doc,
            display_order: index + 1,
        }));

        setDoctors(updatedDoctors); // reflects in table immediately

        try {
            // ✅ pass the doctor_specialities.id in from_id
            await reorderSpecialistDoctors({
                from_id: doctors[oldIndex].doctor_specialities_id,      // this is the main ID
                to_position: newIndex + 1,          // new display order
            });
            toast.success("Doctor reordered successfully.");
        } catch (err) {
            toast.error("Reorder failed.");
        }
    };

    const handleEdit = (doctor) => {
        setSelectedDoctor(doctor);
        setNewPosition(doctor.display_order);
        setEditDialogOpen(true);
    };

    const handleUpdatePosition = async () => {
        try {
            await setDoctorSpecialityPosition({
                doctor_specialities_id: selectedDoctor.doctor_specialities_id,
                display_order: parseInt(newPosition),
            });
            toast.success("Display order updated");
            setEditDialogOpen(false);
            handleFetchDoctors(specialityId);
        } catch {
            toast.error("Failed to update position");
        }
    };

    useEffect(() => {
        if (specialityId) handleFetchDoctors(specialityId);
    }, [specialityId]);

    return (
        <Paper sx={{ padding: 3 }}>
            <Typography variant="h5" gutterBottom>
                Specialist Doctors by Speciality
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Select Speciality</InputLabel>
                <Select
                    value={specialityId}
                    onChange={(e) => setSpecialityId(e.target.value)}
                    label="Select Speciality"
                >
                    {specialties.map((specialty) => (
                        <MenuItem key={specialty.id} value={specialty.id}>
                            {specialty.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={doctors.map((doc) => doc.doctor_specialities_id)} strategy={verticalListSortingStrategy}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Drag</TableCell>
                                <TableCell>Display Order</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Speciality</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {doctors.map((doctor) => (
                                <SortableRow
                                    key={doctor.doctor_specialities_id}
                                    doctor={doctor}
                                    onToggle={handleToggleStatus}
                                    onEdit={handleEdit}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </SortableContext>
            </DndContext>

            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
                <DialogTitle>Edit Display Order</DialogTitle>
                <DialogContent>
                    <TextField
                        label="New Position"
                        type="number"
                        fullWidth
                        value={newPosition}
                        onChange={(e) => setNewPosition(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleUpdatePosition}>Save</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default ListSpecialistDoctors;